<script lang="ts">
  import { fly } from 'svelte/transition';
  import { taskStore } from '$lib/tasks/tasks.svelte';
  import { guardUnsaved } from '$lib/unsavedChanges/unsavedChanges.svelte';

  // The background tasks, one card each (see `taskStore`). Positioned by the
  // host, like `toastList`.
  //
  // A task survives any in-app navigation (it is a promise, not a component),
  // but not a page unload, so only an unload asks while one is running: the
  // empty scope makes every in-app path "inside" it.
  guardUnsaved({
    isDirty: () => taskStore.running,
    scope: () => '',
    title: 'A task is still running',
    message: 'Leaving now cuts it off part-way. Wait for it to finish?'
  });
</script>

<div class="tasks" aria-live="polite">
  {#each taskStore.tasks as task (task.id)}
    <div
      class="task {task.state}"
      role="status"
      in:fly={{ y: -8, duration: 200 }}
      out:fly={{ x: 8, duration: 200 }}
    >
      {#if task.thumb}
        <img class="thumb" src={task.thumb} alt="" />
      {/if}
      <div class="body">
        <div class="head">
          <span class="title">{task.title}</span>
          {#if task.state !== 'running'}
            <button
              class="dismiss"
              type="button"
              aria-label="Dismiss"
              title="Dismiss"
              onclick={() => taskStore.remove(task.id)}
            >
              <span class="dismissIcon"></span>
            </button>
          {/if}
        </div>
        <div class="status">
          {#if task.state === 'done'}<span class="mark done"></span>{/if}
          {#if task.state === 'error'}<span class="mark error"></span>{/if}
          <span class="statusText">{task.status}</span>
          {#if task.state === 'running' && task.progress != null}
            <span class="percent">{Math.round(task.progress * 100)}%</span>
          {/if}
        </div>
        {#if task.state === 'running' || task.state === 'done'}
          <div
            class="bar"
            class:indeterminate={task.progress == null}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={task.progress == null ? undefined : Math.round(task.progress * 100)}
          >
            <div
              class="fill"
              style:width={task.progress == null ? null : `${task.progress * 100}%`}
            ></div>
          </div>
        {/if}
        {#if task.actions.length > 0}
          <div class="actions">
            {#each task.actions as action (action.label)}
              <button class="action" type="button" onclick={action.run}>{action.label}</button>
            {/each}
          </div>
        {/if}
      </div>
    </div>
  {/each}
</div>

<style lang="scss">
  .tasks {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }

  .task {
    display: flex;
    gap: 10px;
    width: 300px;
    padding: 10px 12px;
    border: 1px solid var(--subtile-devider);
    border-radius: var(--border-radius-medium);
    background-color: var(--background-site);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
    font-family: var(--font-primary);

    &.error {
      border-color: var(--warning-bright);
    }
  }

  .thumb {
    flex: none;
    width: 48px;
    height: 32px;
    object-fit: cover;
    border-radius: var(--border-radius-small);
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-width: 0;
  }

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .title {
    @include inkCentered;
    min-width: 0;
    overflow-x: clip;
    overflow-y: visible;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--text-primary);
    font-size: 13px;
    line-height: 18px;
  }

  .dismiss {
    flex: none;
    display: flex;
    padding: 2px;
    border: none;
    background: none;
    cursor: pointer;
  }

  .dismissIcon {
    @include icon;
    --icon-url: url('$lib/icons/close.svg');
    --icon-color: var(--text-secondary);
    --icon-width: 10px;
    --icon-height: 10px;
  }

  // A failure's message can run to a few lines; the mark stays by the first.
  .status {
    display: flex;
    align-items: flex-start;
    gap: 6px;
  }

  .statusText,
  .percent {
    @include inkCentered;
    color: var(--text-secondary);
    font-size: 12px;
    line-height: 16px;
  }

  .statusText {
    flex: 1;
    min-width: 0;
  }

  .percent {
    flex: none;
    font-variant-numeric: tabular-nums;
  }

  .mark {
    @include icon;
    flex: none;
    margin-block: 3px;

    &.done {
      --icon-url: url('$lib/icons/check.svg');
      --icon-color: var(--success-bright);
      --icon-width: 12px;
      --icon-height: 9px;
    }

    &.error {
      --icon-url: url('$lib/icons/close.svg');
      --icon-color: var(--warning-bright);
      --icon-width: 10px;
      --icon-height: 10px;
    }
  }

  .bar {
    position: relative;
    height: 4px;
    overflow: hidden;
    border-radius: 2px;
    background-color: var(--devider);
  }

  .fill {
    height: 100%;
    border-radius: inherit;
    background-color: var(--accent);
    transition: width 200ms linear;

    .done & {
      background-color: var(--success-bright);
    }
  }

  // Nothing to measure (the Getty call, the final save): a sliding segment.
  .indeterminate .fill {
    position: absolute;
    width: 35%;
    animation: slide 1.2s ease-in-out infinite;
  }

  .actions {
    display: flex;
    gap: 12px;
  }

  .action {
    padding: 0;
    border: none;
    background: none;
    color: var(--accent);
    font-family: var(--font-primary);
    font-size: 12px;
    line-height: 16px;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  @keyframes slide {
    from {
      left: -35%;
    }
    to {
      left: 100%;
    }
  }
</style>
